export type CurrentUserScoresScrollInput = {
  bottomInset: number;
  currentScrollY: number;
  leadingContext?: number;
  rowHeight: number;
  rowTopInViewport: number;
  topInset: number;
  viewportHeight: number;
};

const VISIBILITY_SLOP = 1;

/**
 * Scroll offset that brings the signed-in scores row fully on screen.
 * Rows already visible stay put. Rows completely off screen land one row
 * below the sticky header. Rows clipped by an edge move the minimum amount.
 */
export function getCurrentUserScoresScrollY({
  bottomInset,
  currentScrollY,
  leadingContext,
  rowHeight,
  rowTopInViewport,
  topInset,
  viewportHeight,
}: CurrentUserScoresScrollInput): number {
  if (rowHeight <= 0 || viewportHeight <= 0) {
    return currentScrollY;
  }

  const visibleTop = Math.max(0, topInset);
  const availableBottom = viewportHeight - Math.max(0, bottomInset);
  const visibleBottom = Math.min(
    viewportHeight,
    Math.max(visibleTop + rowHeight, availableBottom),
  );
  const rowBottom = rowTopInViewport + rowHeight;
  const fullyVisible =
    rowTopInViewport >= visibleTop - VISIBILITY_SLOP &&
    rowBottom <= visibleBottom + VISIBILITY_SLOP;
  if (fullyVisible) {
    return currentScrollY;
  }

  const maxTop = Math.max(visibleTop, visibleBottom - rowHeight);
  const completelyOffScreen = rowTopInViewport >= visibleBottom || rowBottom <= visibleTop;
  const context = Math.max(0, leadingContext ?? rowHeight);
  const desiredTop = completelyOffScreen
    ? Math.min(visibleTop + context, maxTop)
    : rowBottom > visibleBottom
      ? maxTop
      : visibleTop;

  return Math.max(0, currentScrollY + (rowTopInViewport - desiredTop));
}
